/**
 * Detects old Chromium WebViews (roughly Chrome < 80), as found on cheap or
 * never-updated Android TV boxes (e.g. the TIM box on Android 9).
 *
 * navigator.userAgent can't be used for this: the Android app overrides it
 * with a fake Chrome 120 string. Syntax support can't be faked or polyfilled,
 * so we check whether the engine can parse optional chaining (Chrome 80+).
 *
 * Force it on/off for testing with `?legacy=1` / `?legacy=0` in the URL
 * (before the `#`), or localStorage key "legacyWebView" = "1" / "0".
 */
function detectLegacyWebView(): boolean {
  try {
    const forced =
      new URLSearchParams(window.location.search).get("legacy") ??
      localStorage.getItem("legacyWebView");
    if (forced === "1") return true;
    if (forced === "0") return false;
  } catch {
    // ignore: fall through to feature detection
  }

  try {
    new Function("var a; return a?.b");
    return false;
  } catch {
    return true;
  }
}

export const isLegacyWebView = detectLegacyWebView();

/**
 * Whether the renderer should fall back to Canvas2D instead of WebGL.
 *
 * Some boxes (e.g. the TIM box, Vivante GPU) run an up-to-date WebView but
 * Chrome blocks WebGL on their GPU, so the WebGL renderer has nothing to draw
 * with and the app stays black. The Canvas2D renderer works there, at the cost
 * of shader effects (rounded corners, gradients, ...).
 *
 * Force it with `?renderer=canvas` / `?renderer=webgl` in the URL (before the
 * `#`), or localStorage key "renderer" = "canvas" / "webgl".
 */
function detectCanvasRenderer(): boolean {
  try {
    const forced =
      new URLSearchParams(window.location.search).get("renderer") ??
      localStorage.getItem("renderer");
    if (forced === "canvas") return true;
    if (forced === "webgl") return false;
  } catch {
    // ignore: fall through to feature detection
  }

  try {
    const canvas = document.createElement("canvas");
    const gl =
      canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
    if (!gl) return true;
    // Free the probe context right away; boxes allow only a few at a time.
    (gl as WebGLRenderingContext).getExtension("WEBGL_lose_context")?.loseContext();
    return false;
  } catch {
    return true;
  }
}

export const useCanvasRenderer = detectCanvasRenderer();
